import React, { useEffect, useRef, useState } from "react";
import { openAchievements, type Unopened } from "../../shared/achievements-open";
import { eggBalanceNow, setEggBalance, refreshEggBalance } from "../../shared/shop/useEggBalance";
import { runEggCelebration, RM, streamMsFor } from "../shop/purchase-celebration";
import GoldenEggIcon from "../../shared/GoldenEggIcon";
import musicController from "../../audio/music-controller";
import type { SoundId } from "../../audio/sounds";
import styles from "./OpenAchievements.module.scss";

// Achievement levels waiting on the profile. A waiting card is itself the
// button (OpenableCard); the gold band at the top of the tab opens them all.
// Either way each card pops and its eggs fly out of it into the egg count in
// the header (the shop's egg stream), the count going up as they land, each
// landing with its note.

// The cards that still wait, as the stream's sources (data-open-eggs).
function waitingCards(only?: HTMLElement): { el: HTMLElement; count: number }[] {
  const els = only ? [only] : Array.from(document.querySelectorAll<HTMLElement>("[data-open-eggs]"));
  return els.map((el) => ({ el, count: Number(el.dataset.openEggs) || 0 })).filter((s) => s.count > 0);
}

// A card opening: its medal pops (a ring in its colour, a band of light across
// the card, a crack, a note a step higher for each card in a row), then its
// eggs leave from its tag.
function popCard(card: HTMLElement, k: number) {
  try { musicController.play(`ach-pop-${Math.min(k, 4)}` as SoundId); } catch { /* no audio */ }
  if (RM) return;
  const color = getComputedStyle(card).getPropertyValue("--lv").trim() || "#ffd23f";
  const medal = card.querySelector<HTMLElement>("[data-medal]");
  if (medal) {
    medal.animate([{ transform: "scale(1)" }, { transform: "scale(0.84)", offset: 0.22 }, { transform: "scale(1.16)", offset: 0.58 }, { transform: "scale(1)" }], { duration: 460, easing: "ease-out" });
    const r = medal.getBoundingClientRect();
    const ring = document.createElement("span");
    ring.style.cssText = `position:fixed; left:${r.left}px; top:${r.top}px; width:${r.width}px; height:${r.height}px; border-radius:50%; border:2px solid ${color}; box-shadow:0 0 14px ${color}; pointer-events:none; z-index:1999;`;
    document.body.appendChild(ring);
    ring.animate([{ transform: "scale(0.9)", opacity: 1 }, { transform: "scale(2.1)", opacity: 0 }], { duration: 650, delay: 110, easing: "ease-out", fill: "both" }).onfinish = () => ring.remove();
  }
  const clip = document.createElement("span");
  clip.style.cssText = "position:absolute; inset:0; border-radius:inherit; overflow:hidden; pointer-events:none;";
  const band = document.createElement("span");
  band.style.cssText = "position:absolute; top:0; bottom:0; left:0; width:60%; background:linear-gradient(100deg, transparent, rgba(255,236,170,0.26), transparent);";
  clip.appendChild(band);
  card.appendChild(clip);
  band.animate([{ transform: "translateX(-110%)" }, { transform: "translateX(190%)" }], { duration: 520, delay: 60, easing: "ease-in-out", fill: "both" }).onfinish = () => clip.remove();
  card.querySelector<HTMLElement>("[data-tag]")?.animate([{ transform: "scale(1)", opacity: 1 }, { transform: "scale(1.3)", opacity: 1, offset: 0.4 }, { transform: "scale(0.6)", opacity: 0 }], { duration: 380, delay: 100, easing: "ease-out", fill: "forwards" });
}

let opening = false;
// (`from`: the one card opened; `fallback`: where they come from when no card
// is on the page, the band on the Overview tab)
async function openWithFlight(which: { id: string; level: number }[] | "all", from?: HTMLElement, fallback?: HTMLElement): Promise<void> {
  if (opening) return;
  opening = true;
  try {
    const sources = waitingCards(from);
    const before = eggBalanceNow();
    let eggs = 0, balance: number | null = null;
    const commits: (() => void)[] = [];
    const settle = () => commits.forEach((c) => c());
    for (const w of which === "all" ? ["all" as const] : which) {
      const r = await openAchievements(w, true);
      if ("error" in r) break;
      eggs += r.eggs;
      balance = r.balance;
      commits.push(r.commit);
    }
    if (!eggs) { settle(); refreshEggBalance(); return; }
    if (before == null) { musicController.play("eggs"); settle(); if (balance != null) setEggBalance(balance); else refreshEggBalance(); return; }
    let src = sources.length ? sources : fallback ? [{ el: fallback, count: eggs }] : [];
    if (sources.length) {
      // top to bottom, each card popping just before its eggs leave (from its tag)
      const order = sources.slice().sort((a, b) => a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top);
      const stream = streamMsFor(eggs);
      let n = 0;
      order.forEach((c, k) => { window.setTimeout(() => popCard(c.el, k), (n / Math.max(1, eggs)) * stream); n += c.count; });
      src = order.map((c) => ({ el: c.el.querySelector<HTMLElement>("[data-tag]") ?? c.el, count: c.count }));
      await new Promise((res) => window.setTimeout(res, 150));
    }
    await runEggCelebration({ slot: null, total: eggs, sources: src, notes: true, onProgress: (landed) => setEggBalance(before + landed) });
    settle();
    if (balance != null) setEggBalance(balance); else refreshEggBalance();
  } finally {
    opening = false;
  }
}

// (When the last one is opened the band folds shut, so what is under it slides
// up instead of jumping: it keeps its last words while it closes.)
export function OpenBanner({ unopened }: { unopened: Unopened[] }) {
  const [busy, setBusy] = useState(false);
  const last = useRef(unopened);
  if (unopened.length) last.current = unopened;
  const [shown, setShown] = useState(unopened.length > 0);
  const closing = shown && !unopened.length;
  useEffect(() => {
    if (unopened.length) { setShown(true); return; }
    const id = window.setTimeout(() => setShown(false), 420);
    return () => window.clearTimeout(id);
  }, [unopened.length]);
  if (!shown && !unopened.length) return null;
  const list = last.current;
  const eggs = list.reduce((s, u) => s + u.eggs, 0);
  const open = async (el: HTMLElement) => {
    if (busy) return;
    setBusy(true);
    await openWithFlight("all", undefined, el);
    setBusy(false);
  };
  return (
    <div className={`${styles.fold} ${closing ? styles.folded : ""}`}>
      <div className={styles.foldIn}>
        <button type="button" className={styles.band} onClick={(e) => void open(e.currentTarget)} disabled={busy || closing}>
          <span className={styles.eggs} aria-hidden="true"><GoldenEggIcon size={26} /></span>
          <span className={styles.text}>
            <b>{list.length === 1 ? "1 achievement to open" : `${list.length} achievements to open`}</b>
            <small>+{eggs} <GoldenEggIcon size={12} /></small>
          </span>
          <span className={styles.open}>{closing ? "Opened" : busy ? "Opening" : "Open all"}</span>
        </button>
      </div>
    </div>
  );
}

// An achievement's card. With a level waiting it is itself the button (the
// whole card opens it) and wears what it pays in its corner.
export function OpenableCard({ items, className, style, children }: { items: Unopened[]; className: string; style?: React.CSSProperties; children: React.ReactNode }) {
  const [busy, setBusy] = useState(false);
  if (!items.length) return <div className={className} style={style}>{children}</div>;
  const eggs = items.reduce((s, u) => s + u.eggs, 0);
  const open = async (el: HTMLElement) => {
    if (busy) return;
    setBusy(true);
    await openWithFlight(items.map((u) => ({ id: u.id, level: u.level })), el);
    setBusy(false);
  };
  return (
    <div
      className={`${className} ${styles.card}`}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={`Open, +${eggs} Golden Eggs`}
      data-open-eggs={eggs}
      onClick={(e) => void open(e.currentTarget)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); void open(e.currentTarget); } }}
    >
      {children}
      <span className={styles.tag} aria-hidden="true" data-tag="">+{eggs}<GoldenEggIcon size={14} /></span>
    </div>
  );
}
